import { Organization, Project, WorkspaceResult } from '@/api/';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import { Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteProjectConfirmation } from './DeleteProjectConfirmation';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState, useCallback, ReactNode } from 'react';

/**
 * We'll update the selected project in the store and route
 * to the project page.
 */

// SortableItem Component
function SortableItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}
function handleSelectProject(
  organization: Organization,
  ws: WorkspaceResult,
  project: Project,
) {
  $project.set(project);

  openPage($router, 'project', {
    organizationId: organization.id,
    projectId: project.id,
    workspaceId: ws.workspace.id,
  });
}

/**
 * Runs the project and starts the generation.  We put this on a button
 * so that we can have some control later on so that the user can configure
 * some additional settings like column order and so on.
 * @param project The project to run.
 */
async function handleRunProject(project: Project) {
  await runProject(project.id);
}

async function handleCancelProject(project: Project) {
  await cancelProject(project.id);
}

export const ProjectListing = () => {
  // 1. ALL hooks must be declared here, regardless of state
  const org = useStore($org);
  const ws = useStore($workspace);
  const projects = useStore($projects);
  const loadingProjects = useStore($loadingProjects);
  const externalSourceSyncStatuses = useStore($externalSourceSyncStatuses);
  const selectedProject = useStore($project);
  const downloaded = useStore($downloaded);

  // 2. State hooks
  const [orderedProjects, setOrderedProjects] = useState<Project[]>([]);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false);
  const [projectToBeDeleted, setProjectToBeDeleted] = useState('');
  const [runningProjects, setRunningProjects] = useState<{[key: string]: boolean}>({});

  // 3. Other hooks
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // 4. ALL callback hooks must be declared here, even if they're only used conditionally
  const handleDragEnd = useCallback((event: any) => {
    if (!projects?.length) return; // Early return if no projects
    
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = orderedProjects.findIndex(item => item.id === active.id);
      const newIndex = orderedProjects.findIndex(item => item.id === over.id);

      const newOrderedProjects = arrayMove(orderedProjects, oldIndex, newIndex);
      setOrderedProjects(newOrderedProjects);

      const savedProjectOrdersRaw = localStorage.getItem('projects_orders');
      const savedProjectOrders = JSON.parse(savedProjectOrdersRaw || '{}');

      if (ws && !savedProjectOrders[ws.workspace.id]) {
        savedProjectOrders[ws.workspace.id] = [];
      }

      if (ws) {
        savedProjectOrders[ws.workspace.id] = newOrderedProjects.map(
          project => project.id
        );
      }

      localStorage.setItem(
        'projects_orders',
        JSON.stringify(savedProjectOrders)
      );
      // Log the new order after dragging
      console.log('Updated order of projects:', newOrderedProjects.map(p => p.id));

    }
  }, [projects, orderedProjects, ws]);

  const handleProjectDeleted = useCallback((deletedProjectId: string) => {
    setOrderedProjects(prevOrderedProjects =>
      prevOrderedProjects.filter(p => p.id !== deletedProjectId)
    );

    if (ws) {
      const savedProjectOrdersRaw = localStorage.getItem('projects_orders');
      const savedProjectOrders = JSON.parse(savedProjectOrdersRaw || '{}');
      if (savedProjectOrders[ws.workspace.id]) {
        savedProjectOrders[ws.workspace.id] = savedProjectOrders[ws.workspace.id]
          .filter((id: string) => id !== deletedProjectId);
        localStorage.setItem('projects_orders', JSON.stringify(savedProjectOrders));
      }
    }
  }, [ws]);

  const handleDownloadCsv = useCallback(async (projectId: string) => {
    await downloadProjectResults(projectId);
    toast('Downloaded CSV', {
      description: 'Downloaded the requested CSV',
      duration: 5000,
    });
  }, []);

  const handleRunProjectOptimistic = useCallback(async (project: Project) => {
    setRunningProjects(prev => ({ ...prev, [project.id]: true }));
    try {
      await runProject(project.id);
      toast.success('Project is now running.');
    } catch (error) {
      setRunningProjects(prev => {
        const updated = { ...prev };
        delete updated[project.id];
        return updated;
      });
      toast.error('Failed to run the project. Please try again.');
    }
  }, []);

  // 5. Effects
  useEffect(() => {
    if (projects?.length > 0) {
      const savedProjectOrdersRaw = localStorage.getItem('projects_orders');
      const savedProjectOrders = JSON.parse(savedProjectOrdersRaw || '{}');

      console.log('Saved Project Orders:', savedProjectOrders);

      if (ws && savedProjectOrders[ws.workspace.id]) {
        const projectOrder = savedProjectOrders[ws.workspace.id];
        const projectOrderMap = new Map(
          projectOrder.map((id: string, index: number) => [id, index])
        );

        // **Filter projects that are in savedProjectOrders**
        const sortedProjects = projects
          .filter(project => projectOrderMap.has(project.id))
          .sort((a, b) => {
            const orderA = projectOrderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = projectOrderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return Number(orderA) - Number(orderB);
          });

        console.log('Sorted Projects Based on Saved Order:', sortedProjects.map(p => p.id));

        // **Identify new projects not present in the saved order**
        const newProjects = projects.filter(
          project => !projectOrderMap.has(project.id)
        );

        console.log('New Projects:', newProjects.map(p => p.id));

        // **Sort new projects by creation date in descending order**
        const sortedNewProjects = newProjects.sort(
          (a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime()
        );

        console.log('Sorted New Projects:', sortedNewProjects.map(p => p.id));

        // **Combine sortedNewProjects and sortedProjects without duplication**
        setOrderedProjects([...sortedNewProjects, ...sortedProjects]);

        // **Update localStorage to include new projects at the top**
        if (sortedNewProjects.length > 0) {
          const updatedOrder = sortedNewProjects.map(p => p.id).concat(projectOrder);
          savedProjectOrders[ws.workspace.id] = updatedOrder;
          localStorage.setItem('projects_orders', JSON.stringify(savedProjectOrders));
          console.log('Updated Saved Project Orders:', savedProjectOrders);
        }
      } else {
        // **No saved order exists; sort all projects by creation date descending**
        const sortedAllProjects = projects
          .slice()
          .sort((a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime());

        console.log('No Saved Order. Sorted All Projects by Creation Date:', sortedAllProjects.map(p => p.id));

        setOrderedProjects(sortedAllProjects);
      }
    } else {
      // Initialize with empty array when no projects
      setOrderedProjects([]);
    }
  }, [projects, ws]);

  // Log the order whenever it changes
  useEffect(() => {
    console.log('Current order of projects:', orderedProjects.map(p => p.id));
  }, [orderedProjects]);

  const sortedProjects = projects
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
    );

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);

    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }

  // 6. Render logic
  if (loadingProjects) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>No projects found.</p>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedProjects.map(project => project.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul role="list" className="divide-y divide-border">
            {orderedProjects?.map(project => {
              const externalSourceSyncStatus = externalSourceSyncStatuses.find(
                s => s.project.id === project.id,
              );
              const isRunning = runningProjects[project.id] || false;

              return (
                <SortableItem key={project.id} id={project.id}>
                  <li
                    key={project.id}
                    className="flex items-center justify-between gap-x-6 py-5"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-x-3">
                        <p className="text-sm font-semibold leading-6">
                          {project.name}
                        </p>

                        <Badge
                          variant="outline"
                          className={cn(
                            project.status === 'Unknown' &&
                            'bg-secondary text-foreground',
                            project.status === 'New' && 'bg-primary',
                            project.status === 'Ready' &&
                            'bg-secondary text-foreground',
                            project.status === 'Acquiring' && 'bg-purple-600',
                            project.status === 'Transcribing' && 'bg-blue-600',
                            project.status === 'Processing' && 'bg-yellow-600',
                            project.status === 'Cancelled' &&
                            'bg-secondary !text-red-700',
                            project.status === 'Completed' && 'bg-green-700',
                            project.status === 'Error' && 'bg-red-700',
                            'text-white',
                          )}
                        >
                          {project.status}
                        </Badge>
                      </div>

                      <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                        <p className="whitespace-nowrap">
                          Created on{' '}
                          <time dateTime={project.createdAtUtc}>
                            {new Date(project.createdAtUtc).toLocaleDateString()}
                          </time>
                        </p>
                        <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                          <circle cx={1} cy={1} r={1} />
                        </svg>
                        <p className="truncate">
                          Created by {project.createdBy?.email || 'Unknown'}
                        </p>
                      </div>

                      <div className="mt-1 flex items-center gap-x-2 text-sm leading-5 text-muted-foreground">
                        {/* NOTE: A project with csv will always contain one file (i.e one project resource),
                     projects with external source will not contain any project resource,
                     projects with Audio files can contain multiple audio files,
                     in such case its better to ask the user give same name to the audio files then the below logic will work */}
                        {project?.projectResources &&
                          project?.projectResources.length > 0 && (
                            <div className="overflow-x-auto">
                              <p>
                                {project.projectResources.length === 1
                                  ? 'Filename: '
                                  : 'Filenames: '}
                                {project.projectResources.map((resource, index) => (
                                  <span key={resource.fileName}>
                                    {resource.fileName}
                                    {index <
                                      (project?.projectResources ?? []).length -
                                      1 && ', '}
                                  </span>
                                ))}
                              </p>
                            </div>
                          )}

                        <div className="flex items-start">
                          <div>
                            {project.dataMedium == 'ExternalSource' && (
                              <p className="truncate">
                                {(externalSourceSyncStatus?.lastSyncStatus ===
                                  'InProgress' &&
                                  `Importing data from ${externalSourceSyncStatus?.externalSource}  ${externalSourceSyncStatus?.importStartDate && externalSourceSyncStatus?.importEndDate ? getDateRangeString(externalSourceSyncStatus?.importStartDate, externalSourceSyncStatus?.importEndDate) : ''}`) ||
                                  (externalSourceSyncStatus?.lastSyncStatus ===
                                    'Completed' &&
                                    `Successfully imported data from ${externalSourceSyncStatus?.externalSource} ${externalSourceSyncStatus?.importStartDate && externalSourceSyncStatus?.importEndDate ? getDateRangeString(externalSourceSyncStatus?.importStartDate, externalSourceSyncStatus?.importEndDate) : ''}`) ||
                                  (externalSourceSyncStatus?.lastSyncStatus ===
                                    'Failed' &&
                                    externalSourceSyncStatus?.importedRecords ===
                                    0 &&
                                    `Failed to import data from ${externalSourceSyncStatus?.externalSource}`) ||
                                  (externalSourceSyncStatus?.lastSyncStatus ===
                                    'Failed' &&
                                    externalSourceSyncStatus?.importedRecords > 0 &&
                                    `Partially imported data from ${externalSourceSyncStatus?.externalSource} ${externalSourceSyncStatus?.importStartDate && externalSourceSyncStatus?.importEndDate ? getDateRangeString(externalSourceSyncStatus?.importStartDate, externalSourceSyncStatus?.importEndDate) : ''}`) ||
                                  `Waiting for data from ${externalSourceSyncStatus?.externalSource} ${externalSourceSyncStatus?.importStartDate && externalSourceSyncStatus?.importEndDate ? getDateRangeString(externalSourceSyncStatus?.importStartDate, externalSourceSyncStatus?.importEndDate) : ''}`}
                              </p>
                            )}

                            <div className="flex items-center justify-start">
                              <svg
                                viewBox="0 0 2 2"
                                className="h-0.5 w-0.5 fill-current"
                              >
                                <circle cx={1} cy={1} r={1} />
                              </svg>
                              <p className="ml-2 truncate">
                                {project.dataMedium == 'ExternalSource'
                                  ? `${project.processedRows} rows processed`
                                  : `${project.processedRows} rows of ${project.totalRows} processed `}
                              </p>
                            </div>
                          </div>

                          {project.dataMedium == 'ExternalSource' && (
                            <div className="ml-2 flex items-center justify-start">
                              <svg
                                viewBox="0 0 2 2"
                                className="h-0.5 w-0.5 fill-current"
                              >
                                <circle cx={1} cy={1} r={1} />
                              </svg>
                              <p className="ml-2 truncate">
                                {externalSourceSyncStatus?.importedRecords} rows
                                imported
                                {/* {externalSourceSyncStatus?.totalRecords} rows */}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-none items-center gap-x-4">
                      {project.status === 'Completed' && (
                        <>
                          <Button
                            onClick={e => {
                              if (org && ws) handleSelectProject(org, ws, project);
                            }}
                          >
                            View project
                            <span className="sr-only">, {project.name}</span>
                          </Button>
                          <Button
                            onClick={e => {
                              handleDownloadCsv(project.id);
                            }}
                          >
                            Download CSV
                          </Button>
                        </>
                      )}

                      {
                        // Show the Run button if the project is in a state where it can be run.
                        // If the project is an external source, we need to check if the last sync status
                        // is not in progress or failed.
                        project.dataMedium === 'ExternalSource'
                          ? (project.status === 'New' ||
                            project.status === 'Cancelled' ||
                            project.status === 'Ready') && ( // Include Acquiring status
                            <Button
                              onClick={() => handleRunProject(project)}
                              disabled={
                                project.status === 'Ready' || // Disable if project is already Ready
                                // Disable if project is Acquiring
                                !externalSourceSyncStatus || // Disable if external sync status is not available
                                externalSourceSyncStatus.lastSyncStatus ===
                                'InProgress' || // Disable if external sync is in progress
                                (externalSourceSyncStatus.lastSyncStatus ===
                                  'Failed' &&
                                  externalSourceSyncStatus?.importedRecords === 0) // Disable if sync failed and no records were imported
                              }
                            >
                              {project.status === 'New' ||
                                project.status === 'Cancelled' ? (
                                <Play className="mr-2 h-4 w-4" />
                              ) : (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              )}
                              Run
                            </Button>
                          )
                          : project.dataMedium === 'AudioSource'
                            ? project.status === 'Ready' && (
                              <Button
                                onClick={() =>
                                  handleRunProjectOptimistic(project)
                                } // Optimistic handler
                                disabled={isRunning} // Disable if already running
                                className={cn(
                                  isRunning ? 'bg-yellow-500' : 'bg-blue-500',
                                  'text-white',
                                  'disabled:opacity-50',
                                )}
                              >
                                {isRunning ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <Play className="mr-2 h-4 w-4" />
                                    Run
                                  </>
                                )}
                              </Button>
                            )
                            : (project.status === 'New' ||
                              project.status === 'Cancelled' ||
                              project.status === 'Ready') && ( // Include Acquiring status for non-external source projects
                              <Button
                                onClick={() => handleRunProject(project)}
                                disabled={project.status === 'Ready'} // Disable if project is Ready or Acquiring
                              >
                                {project.status === 'New' ||
                                  project.status === 'Cancelled' ? (
                                  <Play className="mr-2 h-4 w-4" />
                                ) : (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Run
                              </Button>
                            )
                      }

                      {/* {(
                    project.dataMedium === 'AudioSource' &&
                    project.status === 'Processing'
                    || project.status === 'Ready'
                    && (
                      <Button
                        variant="destructive"
                        onClick={() => handleCancelProjectbutton(project)}
                        disabled={cancellingProjects[project.id] || false} // Disable if already cancelling
                      >
                        {cancellingProjects[project.id] ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Okbutton1
                            Cancelling...
                          </>
                        ) : (
                          'Cancel'
                        )}
                      </Button>
                    )

                  )} */}

                      {project.status === 'Processing' && (
                        // || project.status === 'Ready'
                        <Button
                          variant="destructive"
                          onClick={() => handleCancelProject(project)}
                        >
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Cancel
                        </Button>
                      )}

                      {project.status === 'Processing' && (
                        <Button disabled>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing
                        </Button>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon">
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56">
                          <DropdownMenuLabel>Options</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={project.status === 'Processing'}
                            onClick={() => {
                              setProjectToBeDeleted(project.id);
                              setIsDeleteProjectModalOpen(true);
                            }}
                          >
                            Delete Project
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </li>
                </SortableItem>
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>

      <DeleteProjectConfirmation
        isOpen={isDeleteProjectModalOpen}
        handleOpen={setIsDeleteProjectModalOpen}
        projectId={projectToBeDeleted}
        onProjectDeleted={handleProjectDeleted}
      />
    </>
  );
};
